/** Supplement FSSAI masters use Code prefix SUPP. (HS/Nutra/FSDU/FSMP/Pre-Pro). FoSCoS food uses numeric codes. */
export const SUPPLEMENT_FSSAI_CODE_PREFIX = 'SUPP.';

function readCode(item: { code?: unknown; Code?: unknown } | unknown): string {
  if (!item || typeof item !== 'object') {
    return String(item ?? '').trim();
  }
  const row = item as { code?: unknown; Code?: unknown };
  return String(row.code ?? row.Code ?? '').trim();
}

export function isSupplementFssaiCode(code: unknown): boolean {
  return readCode(code)
    .toUpperCase()
    .startsWith(SUPPLEMENT_FSSAI_CODE_PREFIX);
}

export function isFoodFssaiCode(code: unknown): boolean {
  const normalized = readCode(code);
  if (!normalized) {
    // FoSCoS rows always have a code; treat blank as food only when name is not a known supplement label.
    return true;
  }
  return !normalized.toUpperCase().startsWith(SUPPLEMENT_FSSAI_CODE_PREFIX);
}

export function filterSupplementFssaiCategories<T extends { id?: string; code?: string; Code?: string }>(
  categories: T[]
): T[] {
  return (categories || []).filter((item) => isSupplementFssaiCode(item));
}

export function filterFoodFssaiCategories<T extends { id?: string; code?: string; Code?: string }>(
  categories: T[]
): T[] {
  return (categories || []).filter((item) => isFoodFssaiCode(item));
}

/** Keep child rows whose parent id is in the allowed category/sub set, or whose own code is SUPP.*. */
export function filterFssaiChildrenByDomain<T extends { code?: string; Code?: string; parentId?: string; categoryId?: string; subCategoryId?: string }>(
  children: T[],
  allowedParentIds: Set<string>,
  preferSupplement: boolean
): T[] {
  return (children || []).filter((item) => {
    const codeMatch = preferSupplement
      ? isSupplementFssaiCode(item)
      : isFoodFssaiCode(item);
    if (codeMatch && readCode(item)) {
      return true;
    }
    const parentId = String(
      item?.parentId ?? item?.categoryId ?? item?.subCategoryId ?? ''
    );
    return !!parentId && allowedParentIds.has(parentId);
  });
}

export function partitionFssaiHierarchy(
  categories: any[],
  subCategories: any[],
  subSubCategories: any[],
  domain: 'supplement' | 'food'
): {
  categories: any[];
  subCategories: any[];
  subSubCategories: any[];
} {
  const preferSupplement = domain === 'supplement';
  const filteredCategories = preferSupplement
    ? filterSupplementFssaiCategories(categories)
    : filterFoodFssaiCategories(categories);

  const categoryIds = new Set(filteredCategories.map((c) => String(c.id)));
  const filteredSubs = filterFssaiChildrenByDomain(
    subCategories,
    categoryIds,
    preferSupplement
  ).filter((s) => categoryIds.has(String(s.parentId ?? s.categoryId ?? '')));

  const subIds = new Set(filteredSubs.map((s) => String(s.id)));
  const filteredSubSubs = filterFssaiChildrenByDomain(
    subSubCategories,
    subIds,
    preferSupplement
  ).filter((ss) => subIds.has(String(ss.parentId ?? ss.subCategoryId ?? '')));

  return {
    categories: filteredCategories,
    subCategories: filteredSubs,
    subSubCategories: filteredSubSubs,
  };
}
